<template>
  <b-modal id="bv-modal-download-album-choice"
           size="md"
           centered
           hide-header
           @show="onShow">
    <div class="text-center">
      <Icon name="grDownload" :width="36" class="mb-3"/>
      <h1 class="modal-title mb-2">{{ $t('modal_download_album_choice.title') }}</h1>
    </div>

    <div v-if="showCheckboxList" class="mt-4">
      <p class="mb-2">{{ $t('modal_download_album_choice.choose_galleries') }}</p>
      <b-form-group class="mb-0">
        <b-form-checkbox-group v-model="selectedGalleryIds" stacked>
          <b-form-checkbox
              v-for="gallery in selectableGalleries"
              :key="gallery.id"
              :value="gallery.id"
              class="mb-2">
            {{ gallery.name }}
            <span v-if="gallery.count != null" class="text-secondary ml-1">({{ gallery.count }})</span>
          </b-form-checkbox>
        </b-form-checkbox-group>
      </b-form-group>
    </div>

    <template #modal-footer="{ cancel }">
      <div class="w-100">
        <b-button variant="primary" block @click="downloadEmail"
                  :disabled="loading || downloadDisabled" class="mb-2">
          <b-spinner small v-if="loading" class="mr-1"></b-spinner>
          <Icon v-else name="send" :width="18" class="mr-1" vertical-align="sub"/>
          {{ $t('modal_download_album_choice.action_email') }}
        </b-button>
        <b-button variant="link" block @click="cancel()" class="text-secondary">
          {{ $t('modal_download_album_choice.action_cancel') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import EventService from "@/services/Event";
import mediaService from "@/domain/galleries/service";
import galleryTreeService from "@/domain/galleryTree/service";

export default {
  name: "ModalDownloadAlbumChoice",


  props: {
    checkin: {
      type: Object,
      default: null,
    },
    galleryId: {
      type: Number,
      default: null,
    },
    galleries: {
      type: Array,
      default: null,
    },
    fetchGalleries: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      selectedGalleryIds: [],
      fetchedGalleries: null,
    }
  },

  computed: {
    effectiveCheckin() {
      return this.checkin || this.$store.getters['checkinDetail/checkin']
    },
    selectableGalleries() {
      const source = this.galleries || this.fetchedGalleries
      if (!Array.isArray(source)) return []
      const seen = new Set()
      const unique = []
      for (const g of source) {
        if (!g || g.id == null || seen.has(g.id)) continue
        seen.add(g.id)
        const count = g.count != null ? g.count
            : g.media_count != null ? g.media_count
            : g.num_media != null ? g.num_media
            : null
        unique.push({id: g.id, name: g.name, count})
      }
      return unique
    },
    showCheckboxList() {
      return this.selectableGalleries.length > 1
    },
    downloadDisabled() {
      return this.showCheckboxList && this.selectedGalleryIds.length === 0
    },
  },

  methods: {
    async onShow() {
      this.fetchedGalleries = null
      this.selectedGalleryIds = this.selectableGalleries.map(g => g.id)
      if (this.fetchGalleries && !this.galleries && this.effectiveCheckin) {
        try {
          this.loading = true
          this.fetchedGalleries = this.effectiveCheckin.use_gallery_tree
              ? await this.fetchTreeGalleries()
              : await this.fetchFlatGalleries()
          this.selectedGalleryIds = this.selectableGalleries.map(g => g.id)
        } catch (_) {
          this.fetchedGalleries = []
        } finally {
          this.loading = false
        }
      }
    },
    async fetchTreeGalleries() {
      const tree = await galleryTreeService.getGalleryTree(this.effectiveCheckin.id, 0)
      const subs = tree && Array.isArray(tree.sub_gallery) ? tree.sub_gallery : []
      return subs.filter(g => g && g.num_media > 0)
    },
    async fetchFlatGalleries() {
      const response = await mediaService.getGalleries(this.effectiveCheckin.id)
      const flat = []
      Object.values(response || {}).forEach(group => {
        if (!Array.isArray(group)) return
        group.forEach(g => {
          if (g && g.media_count > 0) flat.push(g)
        })
      })
      return flat
    },
    downloadEmail() {
      this.loading = true
      const selection = this.showCheckboxList ? [...this.selectedGalleryIds] : this.galleryId
      EventService.downloadAlbum(this.effectiveCheckin.id, selection)
          .then(() => {
            this.$bvToast.toast(
                this.$t('toast_download_album.text'),
                {
                  title: this.$t('toast_download_album.title'),
                  headerClass: 'd-block pt-2',
                  noCloseButton: false,
                  autoHideDelay: 5000
                })
            this.$bvModal.hide('bv-modal-download-album-choice')
          })
          .catch(error => {
            if (error.response && error.response.status === 400 &&
                (error.response.data.code === 'bulk_download_not_available' || error.response.data.code === 'bulk_download_missing_hd')) {
              this.$bvToast.toast(
                  this.$t('toast_download_album.error'),
                  {variant: 'warning', autoHideDelay: 8000, html: true})
            }
            this.$bvModal.hide('bv-modal-download-album-choice')
          }).finally(() => this.loading = false)
    },
  },
}
</script>
